import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Download, FileText, Loader2, Search, Store, Undo2, X } from 'lucide-react';
import ReportDownloadLog from '@/components/ReportDownloadLog';
import { API, authHeaders, toParams } from '@/components/sales-analytics/api';
import { SAFilters } from '@/components/sales-analytics/SAFilters';
import { OverviewTab } from '@/components/sales-analytics/OverviewTab';
import { StoresTab } from '@/components/sales-analytics/StoresTab';
import { RetailerTab } from '@/components/sales-analytics/RetailerTab';
import { OltownsTab } from '@/components/sales-analytics/OltownsTab';
import { ExceptionsTab } from '@/components/sales-analytics/ExceptionsTab';
import { ServerTable } from '@/components/sales-analytics/ServerTable';
import { ProductsTab } from '@/components/sales-analytics/ProductsTab';
import { MrpTab } from '@/components/sales-analytics/MrpTab';
import { FulfilmentTab } from '@/components/sales-analytics/FulfilmentTab';
import { TXN_COLS } from '@/components/sales-analytics/columns';

const TABS = [
  ['overview', '1. Sales Overview'], ['stores', '2. Store Performance'], ['retailer', '3. Outward Margin (Retailer at MRP)'],
  ['oltowns', '4. Current Margin (OLTOWNS)'], ['products', '5. Product & SKU'], ['mrp', '6. MRP & Price Change'],
  ['fulfilment', '7. Sales Fulfilment'], ['transactions', '8. Transaction Report'], ['exceptions', 'Data Quality & Exceptions'],
];
const DATASET = { stores: 'stores', exceptions: 'exceptions', products: 'products', fulfilment: 'documents' };
const FLAG_LABEL = { sales: 'Sales (invoices + DCs)', negative_margin: 'Negative margin', zero_margin: 'Zero margin', mrp_diff: 'MRP difference',
  missing_pricing: 'Unconfirmed pricing', zero_prm: 'Zero/negative outward margin', critical: 'Critical', missing_mrp_sp: 'Missing MRP/price',
  active_docs: 'Excl. draft/cancelled', mrp_any: 'MRP difference or missing snapshot', master_below_batch: 'Master MRP below batch', master_above_batch: 'Master MRP above batch' };

export default function SalesAnalytics() {
  const navigate = useNavigate();
  const [options, setOptions] = useState(null);
  const [filters, setFilters] = useState({});
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('overview');
  const [history, setHistory] = useState([]);
  const [downloading, setDownloading] = useState(null);
  const [logRefresh, setLogRefresh] = useState(0);
  const snap = useRef({});
  snap.current = { filters, search, tab };

  useEffect(() => {
    axios.get(`${API}/filter-options`, { headers: authHeaders() }).then((r) => setOptions(r.data)).catch(() => toast.error('Failed to load filters'));
  }, []);

  const params = useMemo(() => toParams(filters, search), [filters, search]);

  const pushView = (label) => setHistory((h) => [...h.slice(-19), { ...snap.current, label }]);
  const drill = ({ patch = {}, tab: to = 'transactions' }, label) => {
    pushView(label);
    setFilters((f) => ({ ...f, ...patch }));
    setTab(to);
    toast.info(`Drill-down: ${label}`);
  };
  const goBack = () => {
    const prev = history[history.length - 1];
    if (!prev) return;
    setHistory(history.slice(0, -1));
    setFilters(prev.filters); setSearch(prev.search); setTab(prev.tab);
  };
  const changeTab = (t) => { if (t !== tab) { pushView(`Opened ${TABS.find(([k]) => k === t)?.[1]}`); setTab(t); } };

  const download = async (fmt) => {
    const dataset = DATASET[tab] || 'transactions';
    setDownloading(fmt);
    try {
      const r = await axios.get(`${API}/export.${fmt}`, { headers: authHeaders(), params: { ...params, dataset }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.setAttribute('download', `oltowns_sales_${dataset}_${new Date().toISOString().slice(0, 10)}.${fmt}`);
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Exported ${dataset} (${fmt.toUpperCase()})`);
      setLogRefresh((n) => n + 1);
    } catch (e) {
      toast.error('Export failed');
    }
    setDownloading(null);
  };

  const flags = filters.flag || [];
  return (
    <div className="space-y-5" data-testid="sales-analytics-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/reports')} data-testid="sa-back-reports-btn"><ArrowLeft size={18} /></Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#3b2416] flex items-center gap-2">
              <Store className="text-orange-600" size={28} /> Warehouse to Store Sales Analytics Dashboard
            </h1>
            <p className="text-sm text-stone-600 mt-1">Sales = finalized invoices (FIFO stores) + dispatched delivery challans (FICO stores). Drafts and cancelled documents are excluded. Values incl. GST · dates DD-MM-YYYY.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => download('xlsx')} disabled={!!downloading} className="gap-2 bg-orange-600 hover:bg-orange-700 text-white" data-testid="sa-download-xlsx">
            {downloading === 'xlsx' ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Excel
          </Button>
          <Button onClick={() => download('csv')} disabled={!!downloading} variant="outline" className="gap-2 border-orange-300" data-testid="sa-download-csv">
            {downloading === 'csv' ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />} CSV
          </Button>
        </div>
      </div>

      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10 bg-white border-orange-200"
          placeholder="Search invoice/DC, order number, store, customer, SKU, product, barcode, batch…" data-testid="sa-search-input" />
      </div>

      <SAFilters filters={filters} setFilters={setFilters} options={options} onReset={() => { setFilters({}); setSearch(''); }} />

      {(flags.length > 0 || filters.month) && (
        <div className="flex flex-wrap gap-2" data-testid="sa-drill-chips">
          {filters.month && <Chip label={`Month ${filters.month}`} onClear={() => setFilters((f) => ({ ...f, month: '' }))} testId="sa-chip-month" />}
          {flags.map((fl) => <Chip key={fl} label={FLAG_LABEL[fl] || fl} testId={`sa-chip-${fl}`}
            onClear={() => setFilters((f) => ({ ...f, flag: (f.flag || []).filter((x) => x !== fl) }))} />)}
        </div>
      )}

      {history.length > 0 && (
        <div className="sticky top-0 z-30 py-2 bg-orange-50/90 backdrop-blur-md -mx-1 px-1" data-testid="sa-back-bar">
          <div className="flex items-center gap-3 rounded-lg border border-orange-300 bg-white px-3 py-2 shadow-sm">
            <Button size="sm" onClick={goBack} className="gap-2 bg-[#3b2416] hover:bg-[#2a190f] text-white" data-testid="sa-back-to-previous-btn"><Undo2 size={14} />Back to previous view</Button>
            <span className="text-xs text-stone-500 truncate">Last action: {history[history.length - 1].label} · {history.length} step(s) back</span>
            <button type="button" className="ml-auto text-xs text-stone-500 hover:underline" onClick={() => setHistory([])} data-testid="sa-back-dismiss">Dismiss</button>
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={changeTab}>
        <TabsList className="flex flex-wrap h-auto gap-1 bg-orange-100/70 p-1" data-testid="sa-tabs">
          {TABS.map(([k, label]) => (
            <TabsTrigger key={k} value={k} className="text-xs sm:text-sm data-[state=active]:bg-[#3b2416] data-[state=active]:text-white" data-testid={`sa-tab-${k}`}>{label}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="overview" className="mt-4">{tab === 'overview' && <OverviewTab params={params} onDrill={drill} />}</TabsContent>
        <TabsContent value="stores" className="mt-4">{tab === 'stores' && <StoresTab params={params} onDrill={drill} />}</TabsContent>
        <TabsContent value="retailer" className="mt-4">{tab === 'retailer' && <RetailerTab params={params} onDrill={drill} />}</TabsContent>
        <TabsContent value="oltowns" className="mt-4">{tab === 'oltowns' && <OltownsTab params={params} onDrill={drill} />}</TabsContent>
        <TabsContent value="products" className="mt-4">{tab === 'products' && <ProductsTab params={params} onDrill={drill} />}</TabsContent>
        <TabsContent value="mrp" className="mt-4">{tab === 'mrp' && <MrpTab params={params} onDrill={drill} />}</TabsContent>
        <TabsContent value="fulfilment" className="mt-4">{tab === 'fulfilment' && <FulfilmentTab params={params} onDrill={drill} />}</TabsContent>
        <TabsContent value="transactions" className="mt-4">
          {tab === 'transactions' && <ServerTable endpoint="/transactions" params={params} columns={TXN_COLS} testId="sa-transactions-table" defaultSort="doc_date" />}
        </TabsContent>
        <TabsContent value="exceptions" className="mt-4">{tab === 'exceptions' && <ExceptionsTab params={params} onDrill={drill} />}</TabsContent>
      </Tabs>

      <ReportDownloadLog reportKey={`sales-analytics-${DATASET[tab] || 'transactions'}`} refreshToken={logRefresh} />
    </div>
  );
}

const Chip = ({ label, onClear, testId }) => (
  <span className="inline-flex items-center gap-1 rounded-full bg-[#3b2416] text-white text-xs px-3 py-1" data-testid={testId}>
    {label}<button type="button" onClick={onClear} className="hover:text-orange-300" data-testid={`${testId}-clear`}><X size={12} /></button>
  </span>
);
